import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import PlanningUpload from '../components/planning/PlanningUpload';
import PlanningModal from '../components/modals/PlanningModal';
import WeatherWidget from '../components/widgets/WeatherWidget';
import { getWeatherEmoji, WMO_MAP } from '../utils/climateEngine';
import { formatDateBR } from '../utils/formatters';
import type { Planejamento } from '../types';

interface CalendarioEvento {
  id: string;
  data: string;
  tipo: 'feriado' | 'ponte' | 'reuniao' | 'evento' | 'ferias';
  descricao?: string;
}

interface PeriodoLetivo {
  id?: string;
  inicio_aulas: string;
  ferias_inicio: string;
  ferias_fim: string;
  termino_aulas: string;
  rematricula_inicio?: string | null;
  rematricula_fim?: string | null;
}

const TIPOS_EVENTO = [
  { value: 'feriado', label: 'Feriado', color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
  { value: 'ponte', label: 'Ponte', color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800' },
  { value: 'reuniao', label: 'Reunião', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
  { value: 'evento', label: 'Evento', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' },
] as const;

const Calendario: React.FC = () => {
  const hoje = new Date();
  const hojeLocal = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [eventos, setEventos] = useState<CalendarioEvento[]>([]);
  const [periodo, setPeriodo] = useState<PeriodoLetivo | null>(null);
  const [showPeriodoModal, setShowPeriodoModal] = useState(false);
  const [formPeriodo, setFormPeriodo] = useState<PeriodoLetivo>({
    inicio_aulas: '', ferias_inicio: '', ferias_fim: '', termino_aulas: '',
  });
  const [showRematriculaModal, setShowRematriculaModal] = useState(false);
  const [formRematricula, setFormRematricula] = useState({ rematricula_inicio: '', rematricula_fim: '' });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedDayEventos, setSelectedDayEventos] = useState<CalendarioEvento[]>([]);
  const [novoEventoTipo, setNovoEventoTipo] = useState<string>('evento');
  const [novoEventoDesc, setNovoEventoDesc] = useState('');
  const [planningFiles, setPlanningFiles] = useState<Planejamento[]>([]);
  const [climaDados, setClimaDados] = useState<Record<string, any>>({});
  const [planningDay, setPlanningDay] = useState<string | null>(null);

  const carregarEventos = useCallback(async () => {
    try {
      const res = await api.get(`/calendario?mes=${mes}&ano=${ano}`);
      setEventos(res.data);
    } catch { setEventos([]); }
  }, [mes, ano]);

  const carregarClima = useCallback(async () => {
    try {
      const res = await api.get('/chamadas/clima');
      if (res.data?.ok && res.data?.raw?.daily) {
        const daily = res.data.raw.daily;
        const map: Record<string, any> = {};
        for (let i = 0; i < daily.time.length; i++) {
          map[daily.time[i]] = {
            temp_max: daily.temperature_2m_max?.[i],
            temp_min: daily.temperature_2m_min?.[i],
            code: daily.weather_code?.[i],
            precipitacao: daily.precipitation_probability_max?.[i] || 0,
          };
        }
        setClimaDados(map);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { carregarClima(); }, [carregarClima]);

  const carregarPeriodo = useCallback(async () => {
    try {
      const res = await api.get('/calendario/periodo');
      if (res.data) {
        setPeriodo(res.data);
        setFormPeriodo(res.data);
        setFormRematricula({
          rematricula_inicio: res.data.rematricula_inicio || '',
          rematricula_fim: res.data.rematricula_fim || '',
        });
      }
    } catch { /* ignore */ }
  }, []);

  const carregarArquivos = useCallback(async () => {
    try {
      const res = await api.get('/planejamento');
      setPlanningFiles(res.data);
    } catch { setPlanningFiles([]); }
  }, []);

  useEffect(() => { carregarEventos(); }, [carregarEventos]);
  useEffect(() => { carregarPeriodo(); }, [carregarPeriodo]);
  useEffect(() => { carregarArquivos(); }, [carregarArquivos]);

  const mesAnterior = () => { if (mes === 1) { setMes(12); setAno(a => a - 1); } else { setMes(m => m - 1); } };
  const mesSeguinte = () => { if (mes === 12) { setMes(1); setAno(a => a + 1); } else { setMes(m => m + 1); } };
  const hojeClick = () => { setMes(hoje.getMonth() + 1); setAno(hoje.getFullYear()); };

  const diasNoMes = new Date(ano, mes, 0).getDate();
  const primeiroDiaSemana = new Date(ano, mes - 1, 1).getDay();
  const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

  const diasAnterior = new Date(ano, mes - 1, 0).getDate();

  const getEventosPorData = (data: string) => eventos.filter(e => e.data === data);

  const handleSalvarPeriodo = async () => {
    try {
      await api.post('/calendario/periodo', formPeriodo);
      setPeriodo(formPeriodo);
      setShowPeriodoModal(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao salvar periodo');
    }
  };

  const handleSalvarRematricula = async () => {
    try {
      const payload = {
        ...formPeriodo,
        rematricula_inicio: formRematricula.rematricula_inicio || null,
        rematricula_fim: formRematricula.rematricula_fim || null,
      };
      await api.post('/calendario/periodo', payload);
      setPeriodo(payload);
      setFormPeriodo(payload);
      setShowRematriculaModal(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao salvar janela de rematriculas');
    }
  };

  const handleDayClick = (data: string) => {
    setSelectedDay(data);
    setSelectedDayEventos(getEventosPorData(data));
    setNovoEventoTipo('evento');
    setNovoEventoDesc('');
  };

  const handleDayDoubleClick = (data: string) => {
    setPlanningDay(data);
  };

  const handleAdicionarEvento = async () => {
    if (!selectedDay || !novoEventoTipo) return;
    try {
      await api.post('/calendario/evento', {
        data: selectedDay,
        tipo: novoEventoTipo,
        descricao: novoEventoDesc,
      });
      carregarEventos();
      setSelectedDayEventos(getEventosPorData(selectedDay));
      setNovoEventoDesc('');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao adicionar evento');
    }
  };

  const handleRemoverEvento = async (id: string) => {
    try {
      await api.delete(`/calendario/evento/${id}`);
      carregarEventos();
      setSelectedDayEventos(prev => prev.filter(e => e.id !== id));
    } catch { /* ignore */ }
  };

  const handleAplicarFerias = async () => {
    if (!periodo?.ferias_inicio || !periodo?.ferias_fim) {
      alert('Defina o período de férias primeiro');
      return;
    }
    try {
      await api.post('/calendario/ferias', {
        ferias_inicio: periodo.ferias_inicio,
        ferias_fim: periodo.ferias_fim,
      });
      carregarEventos();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao aplicar ferias');
    }
  };

  const handleRemoverFerias = async () => {
    try {
      await api.delete('/calendario/ferias');
      carregarEventos();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erro ao remover ferias');
    }
  };

  const diasCalendario: React.ReactNode[] = [];
  for (let i = 0; i < primeiroDiaSemana; i++) {
    const dia = diasAnterior - primeiroDiaSemana + i + 1;
    diasCalendario.push(
      <div key={`prev-${i}`} className="p-1.5 text-xs text-gray-300 dark:text-gray-500 text-center">{dia}</div>
    );
  }
  for (let dia = 1; dia <= diasNoMes; dia++) {
    const dataStr = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const diaEventos = getEventosPorData(dataStr);
    const diaSemana = new Date(ano, mes - 1, dia).getDay();
    const isHoje = dataStr === hojeLocal;
    const isPeriodoInicio = periodo?.inicio_aulas === dataStr;
    const isPeriodoFim = periodo?.termino_aulas === dataStr;
    const isFerias = periodo && dataStr >= periodo.ferias_inicio && dataStr <= periodo.ferias_fim;
    const isSelected = selectedDay === dataStr;
    const climaDia = climaDados[dataStr];
    const temAlertaChuva = climaDia?.precipitacao > 60;

    let bgClass = 'hover:bg-gray-100 dark:hover:bg-gray-700';
    if (isSelected) bgClass = 'bg-primary-100 ring-2 ring-primary-400 dark:bg-primary-900/40 dark:ring-primary-500';
    else if (isHoje) bgClass = 'bg-blue-50 font-bold ring-2 ring-blue-400 dark:bg-blue-950/30 dark:ring-blue-500';
    else if (isFerias) bgClass = 'bg-yellow-50 dark:bg-yellow-950/30';

    diasCalendario.push(
      <button
        key={dia}
        onClick={() => handleDayClick(dataStr)}
        onDoubleClick={() => handleDayDoubleClick(dataStr)}
        className={`p-1.5 rounded relative min-h-[52px] flex flex-col ${bgClass} transition-colors`}
      >
        <span className={`self-start ${diaSemana === 0 || diaSemana === 6 ? 'font-bold' : ''} ${isPeriodoInicio || isPeriodoFim ? 'text-white bg-green-500 rounded-full w-6 h-6 flex items-center justify-center text-sm' : 'text-base'}`}>
          {dia}
        </span>
        {climaDia && (
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 self-center flex items-center gap-0.5 font-medium">
            <span className="text-sm" title={WMO_MAP[climaDia.code] || ''}>{getWeatherEmoji(climaDia.code)}</span>
            <span>{Math.round(climaDia.temp_max)}°</span>
            {temAlertaChuva && <span className="text-blue-500 text-[10px]">💧</span>}
          </span>
        )}
        {diaEventos.length > 0 && (
          <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
            {diaEventos.slice(0, 2).map((ev, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${
                ev.tipo === 'feriado' ? 'bg-red-400 dark:bg-red-500' :
                ev.tipo === 'ponte' ? 'bg-orange-400 dark:bg-orange-500' :
                ev.tipo === 'reuniao' ? 'bg-blue-400 dark:bg-blue-500' :
                ev.tipo === 'ferias' ? 'bg-yellow-400 dark:bg-yellow-500' : 'bg-purple-400 dark:bg-purple-500'
              }`} />
            ))}
            {diaEventos.length > 2 && <span className="text-[8px] text-gray-400 dark:text-gray-500">+{diaEventos.length - 2}</span>}
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Calendário</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setFormRematricula({
                rematricula_inicio: periodo?.rematricula_inicio || '',
                rematricula_fim: periodo?.rematricula_fim || '',
              });
              setShowRematriculaModal(true);
            }}
            className="text-sm px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200 hover:bg-emerald-100 transition dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/40"
          >
            Rematrículas
          </button>
          <button
            onClick={() => setShowPeriodoModal(true)}
            className="text-sm px-3 py-1.5 bg-primary-50 text-primary-700 rounded border border-primary-200 hover:bg-primary-100 transition dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800 dark:hover:bg-primary-900/40"
          >
            Período Letivo
          </button>
        </div>
      </div>

      {periodo && (
        <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700 items-center flex-wrap">
          <span>Início: <strong>{formatDateBR(periodo.inicio_aulas) || '---'}</strong></span>
          <span>Férias: <strong>{formatDateBR(periodo.ferias_inicio) || '---'} a {formatDateBR(periodo.ferias_fim) || '---'}</strong></span>
          <span>Término: <strong>{formatDateBR(periodo.termino_aulas) || '---'}</strong></span>
          <span>Rematrículas: <strong>{periodo.rematricula_inicio ? formatDateBR(periodo.rematricula_inicio) : '---'} a {periodo.rematricula_fim ? formatDateBR(periodo.rematricula_fim) : '---'}</strong></span>
          <div className="flex gap-1 ml-auto">
            <button
              onClick={handleAplicarFerias}
              className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded border border-yellow-200 dark:border-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-900/40 transition"
            >
              Aplicar férias
            </button>
            <button
              onClick={handleRemoverFerias}
              className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              Remover férias
            </button>
          </div>
        </div>
      )}

      <WeatherWidget
        data={hojeLocal}
        clima={climaDados[hojeLocal] || climaDados[Object.keys(climaDados)[0]] || null}
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-black/20 border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <button onClick={mesAnterior} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2">&lt;</button>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-800 dark:text-gray-100">{new Date(ano, mes - 1).toLocaleString('pt-BR', { month: 'long' })} {ano}</span>
            <button onClick={hojeClick} className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 px-2 py-0.5 rounded border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30">Hoje</button>
          </div>
          <button onClick={mesSeguinte} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2">&gt;</button>
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-600">
          {diasSemana.map((d, i) => (
            <div key={d} className={`bg-gray-50 dark:bg-gray-900 p-2 text-xs text-center ${i === 0 || i === 6 ? 'font-bold text-gray-600 dark:text-gray-300' : 'font-medium text-gray-500 dark:text-gray-400'}`}>{d}</div>
          ))}
          {diasCalendario}
        </div>
      </div>

      {selectedDay && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-black/20 border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Eventos em {new Date(selectedDay + 'T12:00:00').toLocaleDateString('pt-BR')}
          </h3>

          <div className="flex gap-2 flex-wrap">
            {selectedDayEventos.map((ev) => {
              const tipoInfo = TIPOS_EVENTO.find(t => t.value === ev.tipo);
              return (
                <span key={ev.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${tipoInfo?.color || 'bg-gray-100 dark:bg-gray-700'}`}>
                  {tipoInfo?.label || ev.tipo}
                  {ev.descricao && <span className="text-gray-500 dark:text-gray-400">- {ev.descricao}</span>}
                  <button onClick={() => handleRemoverEvento(ev.id)} className="ml-1 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400">&times;</button>
                </span>
              );
            })}
            {selectedDayEventos.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">Nenhum evento neste dia.</span>}
          </div>

          <div className="flex gap-2 items-end flex-wrap">
            <select
              value={novoEventoTipo}
              onChange={(e) => setNovoEventoTipo(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded"
            >
              {TIPOS_EVENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              type="text"
              value={novoEventoDesc}
              onChange={(e) => setNovoEventoDesc(e.target.value)}
              placeholder="Descrição (opcional)"
              className="text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded flex-1 min-w-[120px]"
            />
            <button onClick={handleAdicionarEvento} className="text-xs px-3 py-1.5 bg-primary-600 dark:bg-primary-500 text-white rounded hover:bg-primary-700 dark:hover:bg-primary-600 transition">
              Adicionar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-black/20 border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Planejamento</h3>
        <PlanningUpload arquivos={planningFiles} onArquivosChange={carregarArquivos} />
      </div>

      {showPeriodoModal && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 flex items-center justify-center z-40" onClick={() => setShowPeriodoModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-black/20 p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Período Letivo</h3>
            <div className="space-y-3">
              {([
                { key: 'inicio_aulas', label: 'Início das aulas' },
                { key: 'ferias_inicio', label: 'Férias (início)' },
                { key: 'ferias_fim', label: 'Férias (término)' },
                { key: 'termino_aulas', label: 'Término das aulas' },
              ] as const).map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
                  <input
                    type="date"
                    value={formPeriodo[key]}
                    onChange={(e) => setFormPeriodo(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowPeriodoModal(false)} className="text-sm px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Cancelar</button>
              <button onClick={handleSalvarPeriodo} className="text-sm px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded hover:bg-primary-700 dark:hover:bg-primary-600 transition">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showRematriculaModal && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 flex items-center justify-center z-40" onClick={() => setShowRematriculaModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-black/20 p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Janela de Rematrículas</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              O modo "Rematrículas" no grid de Alunos só fica disponível dentro deste intervalo.
            </p>
            <div className="space-y-3">
              {([
                { key: 'rematricula_inicio', label: 'Início das rematrículas' },
                { key: 'rematricula_fim', label: 'Fim das rematrículas' },
              ] as const).map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
                  <input
                    type="date"
                    value={formRematricula[key]}
                    onChange={(e) => setFormRematricula(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowRematriculaModal(false)} className="text-sm px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Cancelar</button>
              <button onClick={handleSalvarRematricula} className="text-sm px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {planningDay && (
        <PlanningModal data={planningDay} onClose={() => setPlanningDay(null)} />
      )}
    </div>
  );
};

export default Calendario;
